import { Component, ElementRef, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { IconComponent } from '@kin/spark-ui';
import { SignupCardComponent } from '../ui/signup-card.component';
import { OwnerRelationship, SignupAddressStore } from './signup-address.store';

@Component({
  selector: 'app-property-confirm',
  standalone: true,
  imports: [IconComponent, SignupCardComponent],
  template: `
    <app-signup-card>
      <div class="source">
        <ngk-icon icon="landmark" size="16px" />
        <span>{{ store.appraisalDistrict() }} · Public record</span>
      </div>
      <h2>Confirm this is your property</h2>

      <!-- The public lookup omits owner + legal description, so those rows render only when present -->
      <div class="record">
        @if (ownerName()) {
          <div>
            <div class="label">Owner on record</div>
            <div class="owner">{{ ownerName() }}</div>
          </div>
        }
        <div>
          <div class="label">Parcel #</div>
          <div class="parcel">{{ lookup()?.parcelId }}</div>
        </div>
        @if (lookup()?.legalDescription; as legal) {
          <div class="full">
            <div class="label">Legal description</div>
            <div class="legal">{{ legal }}</div>
          </div>
        }
      </div>

      <div class="question" id="owner-rel-question" #question>Is this you?</div>
      <!-- Locked once signed, like the contact fields: Form 50-162 was signed with this answer.
           Radiogroup exposes the question as the group label, plus its required/invalid state and error. -->
      <div class="options" role="radiogroup" aria-labelledby="owner-rel-question" aria-required="true"
           [attr.aria-invalid]="!!error() || null" [attr.aria-describedby]="error() ? 'owner-rel-error' : null"
           [class.locked]="locked()" [attr.inert]="locked() ? '' : null" [attr.aria-disabled]="locked() || null">
        <label class="option" [class.selected]="ownerRel() === 'self'">
          <input type="radio" name="ownerRel" value="self" [checked]="ownerRel() === 'self'" (change)="select('self')" />
          <div class="option-text"><strong>Yes, I'm the property owner</strong></div>
        </label>
        <label class="option" [class.selected]="ownerRel() === 'co'">
          <input type="radio" name="ownerRel" value="co" [checked]="ownerRel() === 'co'" (change)="select('co')" />
          <div class="option-text"><strong>I'm a co-owner listed on the deed</strong></div>
        </label>
        <label class="option" [class.selected]="ownerRel() === 'entity'">
          <input type="radio" name="ownerRel" value="entity" [checked]="ownerRel() === 'entity'" (change)="select('entity')" />
          <div class="option-text"><strong>The property is owned by my trust or LLC.</strong></div>
        </label>
        <label class="option" [class.selected]="ownerRel() === 'none'">
          <input type="radio" name="ownerRel" value="none" [checked]="ownerRel() === 'none'" (change)="select('none')" />
          <div class="option-text"><strong>None of these apply.</strong></div>
        </label>
      </div>
      @if (error()) { <p class="field-error" id="owner-rel-error" role="alert">{{ error() }}</p> }

      @if (ownerRel() === 'none') {
        <div class="not-owner">
          <ngk-icon icon="info" size="16px" />
          <span>Only the listed owner (or their authorized agent) can protest. Forward this link to {{ ownerName() || 'the owner on record' }} so they can enroll for free.</span>
        </div>
      }
    </app-signup-card>
  `,
  styles: `
    :host { display: block; margin-bottom: 20px; }
    .source { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; color: var(--kin-text-tertiary); }
    .source span { font-size: 12px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; }
    h2 { font-size: 22px; font-weight: 600; line-height: 1.3; margin: 0 0 16px; }
    .record {
      display: grid; grid-template-columns: 1fr 1fr; gap: 12px 20px; padding: 16px;
      background: var(--kin-neutral-100); border: 1px solid var(--kin-border-subtle);
      border-radius: var(--kin-radius-md); margin-bottom: 24px; font-size: 14px;
    }
    .record .full { grid-column: 1 / -1; }
    .label {
      font-size: 12px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase;
      color: var(--kin-text-tertiary); margin-bottom: 4px;
    }
    .owner { font-weight: 700; }
    .parcel { font-variant-numeric: tabular-nums; }
    .legal { color: var(--kin-text-secondary); }
    .question { font-size: 16px; font-weight: 600; margin-bottom: 12px; }
    .options { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
    /* Same as the enroll form's field errors */
    .field-error { color: var(--kin-red-600); font-size: 12px; font-weight: 500; margin: -8px 0 16px; }
    .option {
      display: flex; gap: 12px; align-items: flex-start; padding: 12px 14px; cursor: pointer;
      border: 1px solid var(--kin-neutral-300); background: var(--kin-white); border-radius: var(--kin-radius-md);
      transition: border-color 180ms var(--kin-ease-out), background 180ms var(--kin-ease-out);
    }
    .option:hover { border-color: var(--kin-neutral-400); }
    /* Same treatment as the enroll form's locked contact fields */
    .options.locked { opacity: 0.55; pointer-events: none; }
    .option.selected { border-color: var(--kin-yellow-600); background: var(--kin-yellow-100); }
    .option input { margin-top: 3px; accent-color: var(--kin-neutral-1000); }
    .option-text { font-size: 14px; line-height: 1.5; }
    .option-text strong { font-weight: 600; }
    .option-text span { color: var(--kin-text-secondary); }
    /* Approved pairing 11 tint: Red-100 surface with Neutral-1000 text; icon stays neutral per icon rules */
    .not-owner {
      padding: 12px 14px; background: var(--kin-red-100); border-left: 3px solid var(--kin-red-600);
      border-radius: var(--kin-radius-md); margin-bottom: 16px;
      display: flex; gap: 10px; font-size: 14px; line-height: 1.5;
    }
    .not-owner ngk-icon { flex-shrink: 0; margin-top: 2px; }
    @media (max-width: 600px) {
      .record { grid-template-columns: 1fr; }
    }
  `,
})
export class PropertyConfirmComponent {
  // County public record (owner, parcel, legal description) from the address lookup
  protected store = inject(SignupAddressStore);
  protected readonly lookup = this.store.lookup;
  protected readonly ownerName = computed(() => this.lookup()?.ownerName ?? '');

  // Selected owner relationship (drives option highlight + "none" warning); the enroll save sends it
  readonly ownerRel = this.store.ownerRelationship;
  /** "None of these apply": not the owner, so the page disables enrolling. */
  readonly notOwner = computed(() => this.ownerRel() === 'none');
  /** Once Form 50-162 is signed, the answer it was signed with can't change. */
  readonly locked = input(false);
  /** Emits after each new answer, so the page can update Form 50-162 from it. */
  readonly ownerRelChange = output<OwnerRelationship>();

  private readonly question = viewChild.required<ElementRef<HTMLElement>>('question');
  /** Set once Sign form is pressed unanswered; the error clears as soon as an answer is picked. */
  private readonly answerRequired = signal(false);
  protected readonly error = computed(() =>
    this.answerRequired() && this.ownerRel() === null ? 'Answer "Is this you?" to sign.' : '');

  /** Sign form was pressed unanswered: shows the error and brings the question into view. */
  requireAnswer(): void {
    this.answerRequired.set(true);
    this.question().nativeElement.scrollIntoView({ block: 'center' });
  }

  protected select(rel: OwnerRelationship): void {
    this.ownerRel.set(rel);
    this.ownerRelChange.emit(rel);
  }
}
